//@ts-nocheck
import { EditIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { observer } from "mobx-react-lite";
import Pluralize from "pluralize";
import React, { FC } from "react";
import { number, object, string } from "yup";
import { FormsetWrapper } from "../components/FormWrapper";
import { OverlayRouter } from "../components/OverlayRouter";
import { FFormContext, FFormField } from "../primitives/forms/FormField";
import { IEducationWorkHistoryStore } from "../stores";
import { scrollToTop } from "../utils/functions";
import guarantorInfoData from "./formData/guarantorInfo.json";
import { Formset } from "./forms/FormSet";

export const RawGuarantorsInfoForm = (props: {
  value: any;
  onSubmit: (...params) => void;
  showFormError: boolean;
  innerRef?: any;
}) => {
  const { value, onSubmit, showFormError, innerRef } = props;
  const dialCode = "ng";

  React.useEffect(() => scrollToTop(), []);

  const initialValue = {
    title: "",
    fullName: "",
    email: "",
    phone: "",
    occupation: "",
    company: "",
    relationship: "",
    no_of_years: "",
  };

  let validationSchema = object().shape({
    title: string()
      .trim()
      .required(guarantorInfoData.validationData.title.required),
    fullName: string()
      .trim()
      .required(guarantorInfoData.validationData.fullName.required),
    email: string()
      .required(guarantorInfoData.validationData.email.required)
      .email(guarantorInfoData.validationData.email.valid),
    phone: string().required(guarantorInfoData.validationData.phone.required),
    occupation: string().required(
      guarantorInfoData.validationData.occupation.required,
    ),
    company: string().required(
      guarantorInfoData.validationData.company.required,
    ),
    relationship: string().required(
      guarantorInfoData.validationData.relationship.required,
    ),
    no_of_years: number().required(
      guarantorInfoData.validationData.numberOfYears.required,
    ),
  });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{ ...initialValue, ...value }}
      innerRef={innerRef}
      onSubmit={onSubmit}
      render={({ handleSubmit, errors }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Stack
              spacing={6}
              className="guarantors-info-form"
              id="form-header-section"
            >
              {showFormError ? (
                <Box>
                  <Alert rounded="lg" status="error">
                    <AlertIcon />
                    {guarantorInfoData.formTitle.errorMessage}
                  </Alert>
                </Box>
              ) : (
                <Box>
                  <Alert rounded="lg" status="info">
                    <AlertIcon />
                    {guarantorInfoData.formTitle.alertMessage}
                  </Alert>
                </Box>
              )}

              <Flex
                direction={["column", "row"]}
                justifyContent="space-between"
              >
                <FFormField
                  name="title"
                  type="select"
                  options={guarantorInfoData.titleOptions.map((o) => ({
                    label: o,
                    value: o,
                  }))}
                />

                <Box w={3} h={6} />
                <FFormField name="fullName" />
              </Flex>
              <FFormField name="email" type="email" />
              <FFormField name="phone" type="phone" country_code="ng" />

              <Flex
                direction={["column", "row"]}
                justifyContent="space-between"
              >
                <FFormField name="occupation" />
                <Box width={3} h={6} />
                <FFormField name="company" />
              </Flex>
              <FFormField name="relationship" />
              <FFormField
                name="no_of_years"
                type="select"
                options={guarantorInfoData.numberOfYearsOptions as any}
              />
            </Stack>
          </form>
        );
      }}
    />
  );
};

export const Guarantor = observer(
  ({
    field: guarantor,
    onEdit,
  }: {
    field: {
      title: string;
      fullName: string;
      email: string;
      occupation: string;
      phone: string;
      company: string;
      relationship: string;
      no_of_years: number;
    };
    onEdit: any;
  }) => {
    const { title, email, phone, company, fullName, occupation } = guarantor;

    const emptyFields = Object.values(guarantor).filter((x) => !Boolean(x));
    return (
      <Box>
        {fullName && (
          <Flex justify="space-between" align="baseline">
            <Box flex={1}>
              <Text fontWeight="semibold">{`${title} ${fullName}`}</Text>
              <Text>
                {occupation}, {company}
              </Text>
              <Text>
                {email}, {phone}
              </Text>
              {emptyFields.length > 0 && (
                <Link mt={2} onClick={onEdit} color="red.500" fontSize="xs">
                  Some fields missing. Click to update
                </Link>
              )}
            </Box>

            <Stack ml={[0, 6]} align="center">
              <Button
                size="sm"
                leftIcon={<EditIcon />}
                variant="link"
                colorScheme="blue"
                onClick={onEdit}
              >
                {emptyFields.length > 0 ? "Update" : "Edit"}
              </Button>
              {emptyFields.length > 0 && (
                <Text
                  fontSize="xs"
                  py="1px"
                  px={2}
                  rounded="full"
                  borderWidth="3xs"
                  borderColor="red.500"
                  color="red.500"
                >
                  {Pluralize("error", emptyFields.length, true)}
                </Text>
              )}
            </Stack>
          </Flex>
        )}
      </Box>
    );
  },
);

export type GuarantorProps = {
  store: IEducationWorkHistoryStore;
  spacing?: number;
  onSubmit?: any;
  displayType?: string;
  buttonText?: string;
  shouldDisplayButton?: boolean;
  loading: boolean;
  formsetDescription?: string;
  lockedDescription?: string;
  completed?: boolean;
  formHeader?: string;
  label: string;
  inContainer?: boolean;
  textData: {
    formTitle: { header: string; name: string };
    buttonText: { addText: string; save: string };
  };
};

const GuarantorsData: FC<GuarantorProps> = observer(
  ({
    store,
    textData = guarantorInfoData,
    onSubmit,
    label,
    loading,
    inContainer,
  }) => {
    const [showFormError, setShowFormError] = React.useState(false);
    const render = (
      <OverlayRouter>
        <FFormContext.Provider
          value={{ placeholders: guarantorInfoData.placeholders } as any}
        >
          <Formset
            asModal={false}
            isLoading={loading}
            formsetValues={store.guarantors}
            Component={Guarantor}
            path='/guarantor-detail'
            onEdit={store.editGuarantor}
            onAdd={store.addGuarantor}
            addButtonText={textData.buttonText.addText}
            onSave={async (data: {
              title: string;
              fullName: string;
              ocuupation: string;
              company: string;
              phone: string;
              email: string;
            }) => {
              store.saveGuarantor(data);
              if (onSubmit && !inContainer) {
                await onSubmit(store.formValues, label);
              }
            }}
            validateForm={(values) => {
              let {
                company,
                occupation,
                fullName,
                email,
                relationship,
                no_of_years,
              } = values;
              const isValidated = Boolean(
                company &&
                  occupation &&
                  fullName &&
                  email &&
                  relationship &&
                  no_of_years,
              );
              if (isValidated) {
                setShowFormError(false);
                return isValidated;
              } else {
                setShowFormError(true);
                return isValidated;
              }
            }}
            removeAddButton={store.guarantors.length === 2}
            ComponentForm={RawGuarantorsInfoForm}
            formHeaderText={textData.formTitle.name}
            additionalFormProps={{ showFormError }}
            FormsetHeader={FormsetWrapper}
            currentFormIndex={store.currentGuarantorIndex}
            onRemove={store.removeGuarantor}
            saveButtontext={textData.buttonText.save}
            headerProps={
              inContainer
                ? {
                    hideStep: false,
                    header: textData.formTitle.name,
                    description: textData.formTitle.header,
                    buttonText: textData.buttonText.save,
                    buttonProps: {
                      isLoading: loading,
                      onClick: () => onSubmit(store.formValues),
                    },
                    shouldDisplayButton: true,
                    renderButton: (
                      <Box width="full" mt="60px !important">
                        <Button
                          type="submit"
                          size="lg"
                          onClick={() => {
                            onSubmit(store.formValues);
                          }}
                          isFullWidth
                          colorScheme="blue"
                          isLoading={loading}
                          loadingText="Saving..."
                        >
                          Save and continue
                        </Button>
                      </Box>
                    ),
                  }
                : undefined
            }
          />
        </FFormContext.Provider>
      </OverlayRouter>
    );
    return render;
  },
);

export default GuarantorsData;
